/**
 * Funding & launch costs — B-mode editor for Planisware FIN peer of hours.
 * Edit weekly approved envelope + per-priority launch cost plans, then
 * recalculate affordability for the Launch Calendar.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, DollarSign, Loader2, Plus, Save } from 'lucide-react';
import { ensurePlanningPriorityRow, getScopedPriorities } from '../../../lib/planningEngineService';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { usePlanningEngine } from '../../../pages/planning/engine/PlanningEngineContext';
import {
  deleteCostPlan,
  ensureHorizonFundingEnvelope,
  getCostPlansForScenario,
  getFundingEnvelopes,
  replaceFundingPeriods,
  runFinancialForPlacements,
  setEnvelopeWeeklyApproved,
  upsertCostPlan,
  upsertFundingEnvelope,
} from '../../../lib/timePhasedFinancialService';
import { getLatestPlacements } from '../../../lib/timePhasedPlanningService';
import type { FundingEnvelope, PriorityCostPlan } from '../../../lib/timePhasedFinancial.types';
import { COST_CATEGORIES } from '../../../lib/timePhasedFinancial.types';
import { getItemCountState } from '../../collection/types';
import { formatDollars } from '../../../lib/timePhasedFinancialEngine';

const CARD =
  'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] ';

type CostDraft = {
  id?: string;
  priorityId: string;
  priorityName: string;
  forecastAmount: number;
  approvedAmount: number;
  offsetStartWeeks: number;
  offsetEndWeeks: number;
  costCategory: PriorityCostPlan['costCategory'];
  dirty: boolean;
};

const money = formatDollars;

export function FundingWorkbenchPanel({
  projectId,
  scenarioId,
  horizonStart,
  horizonEnd,
  planningWeeks,
  onSaved,
}: {
  projectId: string;
  scenarioId: string;
  horizonStart: string | null;
  horizonEnd: string | null;
  planningWeeks: number;
  onSaved?: (message: string) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [envelope, setEnvelope] = useState<FundingEnvelope | null>(null);
  const [envelopeName, setEnvelopeName] = useState('Portfolio funding');
  const [weeklyApproved, setWeeklyApproved] = useState(12000);
  const [periodEdits, setPeriodEdits] = useState<Record<string, number>>({});
  const [showWeeks, setShowWeeks] = useState(false);
  const [costDrafts, setCostDrafts] = useState<CostDraft[]>([]);
  const [search, setSearch] = useState('');
  const [affordNote, setAffordNote] = useState<string | null>(null);
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const { scopedItemIds } = usePlanningEngine();

  const start =
    horizonStart ?? new Date().toISOString().slice(0, 10);
  const end =
    horizonEnd ??
    (() => {
      const d = new Date(`${start}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() + planningWeeks * 7);
      return d.toISOString().slice(0, 10);
    })();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchItems();
      const liveItems = useItemsStore.getState().items;
      const [envs, plans, scoped] = await Promise.all([
        getFundingEnvelopes(scenarioId),
        getCostPlansForScenario(projectId, scenarioId),
        getScopedPriorities(projectId, liveItems, scenarioId, scopedItemIds),
      ]);
      const nameById = new Map(scoped.map((p) => [p.id, p.name]));

      let env = envs[0] ?? null;
      if (!env) {
        env = await ensureHorizonFundingEnvelope(
          projectId,
          scenarioId,
          start,
          end,
          12000,
          'Portfolio funding',
        );
      }
      setEnvelope(env);
      setEnvelopeName(env.name);
      const avg =
        env.periods.length > 0
          ? env.periods.reduce((s, p) => s + p.approvedAmount, 0) / env.periods.length
          : 12000;
      setWeeklyApproved(Math.round(avg));
      const pe: Record<string, number> = {};
      for (const p of env.periods) pe[p.bucketStart] = p.approvedAmount;
      setPeriodEdits(pe);

      const byPriority = new Map(plans.map((c) => [c.priorityId, c]));
      const drafts: CostDraft[] = scoped.map((p) => {
        const existing = byPriority.get(p.id);
        if (existing) {
          return {
            id: existing.id,
            priorityId: p.id,
            priorityName: p.name,
            forecastAmount: existing.forecastAmount,
            approvedAmount: existing.approvedAmount ?? existing.forecastAmount,
            offsetStartWeeks: existing.offsetStartWeeks,
            offsetEndWeeks: existing.offsetEndWeeks,
            costCategory: existing.costCategory,
            dirty: false,
          };
        }
        return {
          priorityId: p.id,
          priorityName: p.name,
          forecastAmount: 0,
          approvedAmount: 0,
          offsetStartWeeks: -4,
          offsetEndWeeks: 0,
          costCategory: 'other',
          dirty: false,
        };
      });
      // Include orphan cost plans (priority filtered out)
      for (const plan of plans) {
        if (drafts.some((d) => d.priorityId === plan.priorityId)) continue;
        drafts.push({
          id: plan.id,
          priorityId: plan.priorityId,
          priorityName: nameById.get(plan.priorityId) ?? plan.priorityId.slice(0, 8),
          forecastAmount: plan.forecastAmount,
          approvedAmount: plan.approvedAmount ?? plan.forecastAmount,
          offsetStartWeeks: plan.offsetStartWeeks,
          offsetEndWeeks: plan.offsetEndWeeks,
          costCategory: plan.costCategory,
          dirty: false,
        });
      }
      setCostDrafts(drafts);
      setHasUnsavedEdits(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load funding');
    } finally {
      setLoading(false);
    }
  }, [projectId, scenarioId, start, end, scopedItemIds, fetchItems]);

  useEffect(() => {
    void load();
  }, [load]);

  // Typed-but-unsaved edits live only in React state; refresh/close would
  // silently discard them. Make leaving an explicit choice instead.
  useEffect(() => {
    if (!hasUnsavedEdits) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedEdits]);

  const filteredCosts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return costDrafts;
    return costDrafts.filter((d) => d.priorityName.toLowerCase().includes(q));
  }, [costDrafts, search]);

  const costCountState = getItemCountState(costDrafts.length);
  const periodList = envelope?.periods ?? [];

  const updateDraft = (priorityId: string, patch: Partial<CostDraft>) => {
    setHasUnsavedEdits(true);
    setCostDrafts((rows) =>
      rows.map((r) =>
        r.priorityId === priorityId ? { ...r, ...patch, dirty: true } : r,
      ),
    );
  };

  const handleSave = async () => {
    if (!envelope) {
      setError('Funding envelope has not loaded — nothing was saved. Reload the page and try again.');
      return;
    }
    setSaving(true);
    setError(null);
    setAffordNote(null);
    try {
      await upsertFundingEnvelope({
        id: envelope.id,
        projectId,
        scenarioId,
        name: envelopeName.trim() || 'Portfolio funding',
        currency: envelope.currency || 'USD',
        fundingSource: envelope.fundingSource,
        notes: envelope.notes,
      });

      const periods = periodList.map((p) => ({
        bucketStart: p.bucketStart,
        bucketEnd: p.bucketEnd,
        approvedAmount: periodEdits[p.bucketStart] ?? p.approvedAmount,
        forecastAmount: p.forecastAmount,
      }));
      await replaceFundingPeriods(envelope.id, periods);

      for (const d of costDrafts) {
        if (!d.dirty && d.id) continue;
        if (!d.id && d.forecastAmount <= 0) continue;
        if (d.id && d.forecastAmount <= 0) {
          await deleteCostPlan(d.id);
          continue;
        }
        if (!d.id) {
          // Live-item priorities may have no planning_priorities row yet; the
          // cost plan FK requires one (§4.7 overlay on items.id).
          await ensurePlanningPriorityRow(projectId, d.priorityId, d.priorityName);
        }
        await upsertCostPlan({
          id: d.id,
          projectId,
          priorityId: d.priorityId,
          name: `${d.priorityName} launch cost`,
          currency: 'USD',
          costCategory: d.costCategory,
          capexOpex: 'opex',
          estimateAmount: d.forecastAmount,
          approvedAmount: d.approvedAmount,
          forecastAmount: d.forecastAmount,
          commitmentAmount: null,
          actualAmount: null,
          phasingMode: 'launch_relative',
          offsetStartWeeks: d.offsetStartWeeks,
          offsetEndWeeks: d.offsetEndWeeks,
          notes: 'Edited in Funding workbench',
        });
      }

      const placements = await getLatestPlacements(scenarioId);
      const financial = await runFinancialForPlacements(
        projectId,
        scenarioId,
        start,
        end,
        placements,
      );
      const a = financial.affordability;
      const note = a.withinEnvelope
        ? `Saved. Forecast ${money(a.totalForecastDemand)} fits approved ${money(a.totalApproved)}.`
        : `Saved. Portfolio exceeds funding by ${money(a.overageTotal)} (approved ${money(a.totalApproved)} vs forecast ${money(a.totalForecastDemand)}).`;
      setAffordNote(note);
      onSaved?.(note);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const applyFlatWeekly = async () => {
    if (!envelope || periodList.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await setEnvelopeWeeklyApproved(
        envelope.id,
        periodList.map((p) => ({
          bucketStart: p.bucketStart,
          bucketEnd: p.bucketEnd,
          approvedAmount: p.approvedAmount,
          forecastAmount: p.forecastAmount,
        })),
        weeklyApproved,
      );
      const pe: Record<string, number> = {};
      for (const p of periodList) pe[p.bucketStart] = weeklyApproved;
      setPeriodEdits(pe);
      onSaved?.(`Applied ${money(weeklyApproved)}/week across ${periodList.length} weeks.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to apply weekly amount');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`${CARD} p-8 flex items-center justify-center gap-2 text-sm text-[var(--text-ter)]`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading funding…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={`${CARD} p-4 flex flex-col sm:flex-row sm:items-start gap-3`}>
        <div className="h-9 w-9 bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center shrink-0">
          <DollarSign className="h-5 w-5 text-primary-700 dark:text-primary-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Funding &amp; launch costs
          </h3>
          <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-1">
            Set the weekly approved envelope (shared budget) and the launch cost for each
            priority. This is the money peer of the supply calendar — change it here, then open
            Launch Calendar to see affordability.
          </p>
        </div>
        <Link
          to="/planning/launch-calendar"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline shrink-0"
        >
          Launch Calendar
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {error && (
        <div className={`${CARD} p-3 text-sm text-[var(--coral)] dark:text-[var(--coral)]`}>{error}</div>
      )}
      {affordNote && (
        <div className={`${CARD} p-3 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]`}>
          {affordNote}
        </div>
      )}

      {/* Envelope */}
      <div className={`${CARD} p-4 space-y-4`}>
        <h4 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wider">
          Approved funding envelope
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">Name</span>
            <input
              type="text"
              value={envelopeName}
              onChange={(e) => {
                setHasUnsavedEdits(true);
                setEnvelopeName(e.target.value);
              }}
              className="mt-1 w-full px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
            />
          </label>
          <label className="block text-sm">
            <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">Weekly approved ($)</span>
            <div className="mt-1 flex gap-2">
              <input
                type="number"
                min={0}
                step={500}
                value={weeklyApproved}
                onChange={(e) => {
                  setHasUnsavedEdits(true);
                  setWeeklyApproved(Number(e.target.value) || 0);
                }}
                className="w-full px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] font-mono text-base"
              />
              <button
                type="button"
                disabled={saving || periodList.length === 0}
                onClick={() => void applyFlatWeekly()}
                className="shrink-0 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] disabled:opacity-50"
              >
                Apply to all weeks
              </button>
            </div>
          </label>
        </div>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          {periodList.length} week{periodList.length === 1 ? '' : 's'} · horizon {start} → {end} ·
          total approved if flat:{' '}
          <span className="font-mono text-sm">
            {money(weeklyApproved * Math.max(periodList.length, 1))}
          </span>
        </p>
        <button
          type="button"
          onClick={() => setShowWeeks((v) => !v)}
          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
        >
          {showWeeks ? 'Hide week-by-week amounts' : 'Edit week-by-week amounts'}
        </button>
        {showWeeks && periodList.length > 0 && (
          <div className="max-h-64 overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {periodList.map((p) => (
              <div
                key={p.bucketStart}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
              >
                <span className="font-mono text-base text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                  {p.bucketStart}
                </span>
                <input
                  type="number"
                  min={0}
                  step={500}
                  value={periodEdits[p.bucketStart] ?? p.approvedAmount}
                  onChange={(e) => {
                    setHasUnsavedEdits(true);
                    setPeriodEdits((prev) => ({
                      ...prev,
                      [p.bucketStart]: Number(e.target.value) || 0,
                    }));
                  }}
                  className="w-36 px-2 py-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] font-mono text-base text-right"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cost plans */}
      <div className={`${CARD} p-4 space-y-3`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h4 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wider">
            Launch cost plans
          </h4>
          {costCountState === 'many' && (
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search priorities…"
              className="w-full sm:max-w-xs px-3 py-2 text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
            />
          )}
        </div>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Forecast $ is phased onto the launch window (default: 4 weeks before launch through
          launch week). Set forecast to 0 and save to remove a cost plan.
        </p>

        {costCountState === 'zero' ? (
          <div className="py-6 text-center text-sm text-[var(--text-ter)]">
            No priorities in this scenario yet. Add priorities first, then assign launch costs.
            <Link
              to="/planning/engine/priorities"
              className="block mt-2 text-primary-600 hover:underline"
            >
              Open priorities →
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] border-b border-[var(--rule)] dark:border-[var(--rule)]">
                  <th className="py-2 pr-3 font-medium">Priority</th>
                  <th className="py-2 pr-3 font-medium">Forecast $</th>
                  <th className="py-2 pr-3 font-medium">Approved $</th>
                  <th className="py-2 pr-3 font-medium">Category</th>
                  <th className="py-2 pr-3 font-medium">Weeks before</th>
                  <th className="py-2 font-medium">Through wk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {filteredCosts.map((d) => (
                  <tr key={d.priorityId} className={d.dirty ? 'bg-primary-50/40 dark:bg-primary-900/10' : ''}>
                    <td className="py-2 pr-3 font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] max-w-[12rem] truncate">
                      {d.priorityName}
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        min={0}
                        step={1000}
                        value={d.forecastAmount}
                        onChange={(e) =>
                          updateDraft(d.priorityId, {
                            forecastAmount: Number(e.target.value) || 0,
                          })
                        }
                        className="w-28 px-2 py-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] font-mono text-base"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        min={0}
                        step={1000}
                        value={d.approvedAmount}
                        onChange={(e) =>
                          updateDraft(d.priorityId, {
                            approvedAmount: Number(e.target.value) || 0,
                          })
                        }
                        className="w-28 px-2 py-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] font-mono text-base"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <select
                        value={d.costCategory}
                        onChange={(e) =>
                          updateDraft(d.priorityId, {
                            costCategory: e.target.value as PriorityCostPlan['costCategory'],
                          })
                        }
                        className="px-2 py-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
                      >
                        {COST_CATEGORIES.map((c) => (
                          <option key={c.key} value={c.key}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        max={0}
                        value={d.offsetStartWeeks}
                        onChange={(e) =>
                          updateDraft(d.priorityId, {
                            offsetStartWeeks: Number(e.target.value) || 0,
                          })
                        }
                        className="w-20 px-2 py-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] font-mono text-base"
                        title="Offset start weeks relative to launch (negative = before)"
                      />
                    </td>
                    <td className="py-2">
                      <input
                        type="number"
                        value={d.offsetEndWeeks}
                        onChange={(e) =>
                          updateDraft(d.priorityId, {
                            offsetEndWeeks: Number(e.target.value) || 0,
                          })
                        }
                        className="w-20 px-2 py-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] font-mono text-base"
                        title="Offset end weeks relative to launch"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            const blank = costDrafts.find((d) => d.forecastAmount === 0 && !d.id);
            if (blank) {
              updateDraft(blank.priorityId, { forecastAmount: 25000, approvedAmount: 22500 });
            }
          }}
          className="inline-flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          <Plus className="h-3.5 w-3.5" />
          Suggest $25k on first empty row
        </button>
      </div>

      <div className="flex flex-wrap gap-2 sticky bottom-0 py-3 bg-[var(--bg,white)] dark:bg-[var(--bg)] border-t border-[var(--rule)] dark:border-[var(--rule)]">
        <button
          type="button"
          disabled={saving}
          onClick={() => void handleSave()}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save funding &amp; recalculate
        </button>
        <Link
          to="/planning/launch-calendar"
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)]"
        >
          Open Launch Calendar
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <button
          type="button"
          disabled={saving}
          onClick={() => void load()}
          className="inline-flex items-center gap-2 px-3 py-2.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]"
        >
          Discard unsaved
        </button>
      </div>
    </div>
  );
}
